export const categoryOrder = [
  'Academic',
  'Finance',
  'People',
  'Operations',
];

export const documents = [
  {
    slug: 'admission',
    route: '/admission',
    title: 'Admission',
    icon: 'HiOutlineUsers',
    category: 'Academics',
    format: 'DOCX',
    description:
      'Manage admission registers, application intake, eligibility rules, and student onboarding workflows.',
    metrics: [
      { label: 'Primary focus', value: 'Admissions intake' },
      { label: 'Core records', value: 'Registers and applications' },
      { label: 'Workflow', value: 'Draft to done' },
    ],
    workflow: [
      'Create an admission register with dates, course details, and fee settings.',
      'Confirm the register and start receiving student applications.',
      'Move approved applicants into the admission process and close the cycle after enrollment.',
    ],
    sections: [
      {
        id: 'registers',
        title: 'Admission Registers',
        body: [
          'Admission registers define the intake window for a course, including dates, fee structure, student capacity, and age rules.',
        ],
        bullets: [
          'Set the register name, linked course, start date, end date, and term.',
          'Maintain course fees, minimum admissions, maximum admissions, and minimum required age.',
          'Use the register workflow to confirm, start applications, start admissions, cancel, or mark the process done.',
        ],
      },
      {
        id: 'applications',
        title: 'Applications',
        body: [
          'Applications capture the student-facing admission record and connect the applicant to the selected admission register.',
        ],
        bullets: [
          'Store student identity fields such as photo, title, and complete name.',
          'Track application number, application date, admission date, due date, fee term, discount, and partner mapping.',
          'Maintain educational history including previous institute, previous course, and prior score details.',
        ],
      },
      {
        id: 'enrollment-controls',
        title: 'Enrollment Controls',
        body: [
          'The admission process stays structured by combining register limits with application-level fee and document tracking.',
        ],
        bullets: [
          'Use the register to control which course is open for intake.',
          'Track due dates and fee configuration before enrollment is confirmed.',
          'Keep a clean handoff from application collection to final enrollment activity.',
        ],
      },
    ],
    related: ['student', 'parent', 'faculty'],
  },
  {
    slug: 'assignments',
    route: '/assignments',
    title: 'Assignments',
    icon: 'HiOutlineClipboardDocumentList',
    category: 'Academics',
    format: 'DOCX',
    description:
      'EduX provides facilities to configure assignment types, create assignments, allocate students, review submissions, and manage assignment workflows from draft through final review states.',
    metrics: [
      { label: 'Primary focus', value: 'Assignment management' },
      { label: 'Core records', value: 'Types, assignments, submissions' },
      { label: 'Lifecycle', value: 'Draft to review states' },
    ],
    workflow: [
      'Configure assignment types from Assignments -> Configuration -> Assignment Types.',
      'Create the assignment with course, batch, subject, faculty, reviewer, dates, marks, and description.',
      'Allocate the assignment to students, publish it, and finish it when the work is completed.',
      'Review student submissions and move them through submitted, accepted, change required, resubmit, or rejected states.',
    ],
    sections: [
      {
        id: 'assignment-types',
        title: 'Assignment Types',
        body: [
          'EduX provides the facility to configure the assignment type. List of all the assignment type are displayed here.',
          'Go to Assignments -> Configuration -> Assignment Types.',
          'The below image shows the assignment type.',
        ],
        figure: {
          src: '/assets/doc-images/assignments/image1.png',
          alt: 'Assignment type record showing name, code, and type',
          caption:
            'Assignment Type form showing the assignment type name, code, and type fields.',
        },
        detailCards: [
          {
            title: 'Details of Assignment Types',
            bullets: [
              'Name: specify the name of the assignment type.',
              'Code: you can also provide code of assignment type.',
              'Type: specify the type of assignment being created.',
            ],
          },
        ],
      },
      {
        id: 'assignments',
        title: 'Assignments',
        body: [
          'EduX provides the facility to create the assignments. All the assignments are displayed here.',
          'Go to Assignments -> Assignments -> Assignments.',
          'The below image shows the assignment detail.',
        ],
        figure: {
          src: '/assets/doc-images/assignments/image2.png',
          alt: 'Assignment form showing course, batch, subject, faculty, reviewer, and tabs',
          caption:
            'Assignment form showing the assignment header, faculty and reviewer details, state bar, and the Basic Information, Allocation Information, and Submissions tabs.',
        },
        detailCards: [
          {
            title: 'Assignment Details',
            bullets: [
              'Name: specify the name of the assignment.',
              'Course: select the course from the list for which assignment is created.',
              'Batch: based on the selected course list of batch appears, select appropriate batch from the list.',
              'Subject: select the subject from the list whose assignment is created.',
              'Faculty: select the faculty who has created the assignment from the Faculty list.',
              'Assignment Type: specify the type of assignment being created.',
              'Reviewer: specify the name of the person from the Reviewer list who will review the assignment.',
              'Submission (Smart Button): list of all the submitted assignments by the students appears here.',
            ],
          },
        ],
        subsections: [
          {
            id: 'basic-information',
            title: 'Basic Information',
            body: [
              'The Basic Information page captures the assignment timeline, marks, and description for the assignment record.',
            ],
            bullets: [
              'Issued Date: Issued Date is the date on which assignment is issued. By default today’s date and time is set here.',
              'Submission Date: Submission Date is the date on which assignment is to be submitted.',
              'Marks: allocated the marks for the assignment created.',
              'Description: specify the details of assignment in description field.',
            ],
          },
          {
            id: 'allocation-information',
            title: 'Allocation Information',
            body: [
              'Specify the list of students to whom the assignment is to allocated, i.e all the students who will perform the assignment.',
            ],
          },
          {
            id: 'assignment-submissions-tab',
            title: 'Submissions',
            body: [
              'Click on the Submissions button and show list of all the submitted assignments by the students appears here.',
            ],
            figure: {
              src: '/assets/doc-images/assignments/image3.png',
              alt: 'Submissions tab inside the assignment form',
              caption:
                'Submissions tab inside the assignment form showing linked student submissions with submission date, state, marks, and note fields.',
            },
          },
        ],
      },
      {
        id: 'assignment-workflow',
        title: 'Workflow of Assignments',
        body: [
          'When assignment is created its in Draft state.',
        ],
        figure: {
          src: '/assets/doc-images/assignments/image4.png',
          alt: 'Published assignment showing publish and finish workflow states',
          caption:
            'Assignment record showing the workflow actions and the state bar moving from Draft to Published to Finished.',
        },
        subsections: [
          {
            id: 'assignment-draft-state',
            title: 'Draft',
            body: ['When assignment is created it starts in Draft state.'],
          },
          {
            id: 'assignment-published-state',
            title: 'Published',
            body: [
              'Click on the Publish button to publish the assignment. It changes the state from Draft to Published.',
            ],
          },
          {
            id: 'assignment-finished-state',
            title: 'Finished',
            body: [
              'Click on Finish button if the assignment is completed. It changes the state from Published to Finished.',
            ],
          },
        ],
      },
      {
        id: 'assignment-submissions',
        title: 'Assignment Submissions',
        body: [
          'EduX provides the Assignment Submission facility. List of all the assignments submitted by the students are displayed here.',
          'Go to Assignments -> Assignments -> Submissions.',
          'The below image shows the submitted assignments.',
        ],
        figure: {
          src: '/assets/doc-images/assignments/image5.png',
          alt: 'Assignment submissions list showing assignment, student, date, and state',
          caption:
            'Assignment Submissions list showing the assignment name, student, submission date, and submission state.',
        },
        detailCards: [
          {
            title: 'Details of Assignment Submissions',
            bullets: [
              'Student: specify the student whose assignment is to be submitted.',
              'Assignment: select the name of the assignment from the list of available assignments or create a new assignment.',
              'Submission Date: by default todays date and time is set in the Submission Date.',
              'Description and Note: you can also provide description and note about the assignment.',
            ],
          },
        ],
      },
      {
        id: 'submission-workflow',
        title: 'Workflow of Assignment Submissions',
        body: [
          'Assignment submissions move through review states based on whether the work is accepted, requires changes, or is rejected.',
        ],
        subsections: [
          {
            id: 'submission-draft-state',
            title: 'Draft',
            body: ['When assignment is created, it is in Draft state.'],
          },
          {
            id: 'submission-submitted-state',
            title: 'Submitted',
            body: [
              'Click on the Submit button to submit the assignment. It changes the state from Draft to Submitted.',
            ],
            figure: {
              src: '/assets/doc-images/assignments/image6.png',
              alt: 'Submitted assignment submission with accept, change require, and reject actions',
              caption:
                'Assignment submission record in Submitted state showing the Accept, Change Require, and Reject actions.',
            },
          },
          {
            id: 'submission-accepted-state',
            title: 'Accepted',
            body: [
              'Click on Accept button if the assignment is correct. It changes the state from Submitted to Accepted.',
            ],
            gallery: [
              {
                src: '/assets/doc-images/assignments/image7.png',
                alt: 'Assignment submission state bar in Accepted state',
                caption: 'State bar showing the submission moved to Accepted.',
              },
            ],
          },
          {
            id: 'submission-change-required-state',
            title: 'Change Required',
            body: [
              'If some changes are required in the assignment then click on Change Require button. It changes the state from Submitted to Change Req.',
              'After changes click on Change and Resubmit to submit the assignment after the changes. It changes the state from Change Req. to Draft.',
            ],
            gallery: [
              {
                src: '/assets/doc-images/assignments/image8.png',
                alt: 'Submitted assignment submission before selecting Change Require',
                caption: 'Submitted assignment submission ready for a Change Require review action.',
              },
              {
                src: '/assets/doc-images/assignments/image9.png',
                alt: 'Assignment submission in Change Required state with Change And Resubmit action',
                caption:
                  'Submission in Change Required state showing the Change And Resubmit action.',
              },
            ],
          },
          {
            id: 'submission-rejected-state',
            title: 'Reject',
            body: [
              'You can also reject the assignment by clicking the Reject button. It changes to Rejected state.',
            ],
            gallery: [
              {
                src: '/assets/doc-images/assignments/imagea.png',
                alt: 'Submitted assignment submission showing the Reject action',
                caption: 'Submitted assignment submission showing the Reject action.',
              },
              {
                src: '/assets/doc-images/assignments/imageb.png',
                alt: 'Assignment submission in Rejected state',
                caption: 'State bar showing the submission moved to Rejected.',
              },
            ],
          },
        ],
      },
    ],
    related: ['faculty', 'student', 'exam'],
  },
  {
    slug: 'attendance',
    route: '/attendance',
    title: 'Attendance',
    icon: 'HiOutlineCalendarDays',
    category: 'Academics',
    format: 'DOCX',
    description:
      'Create attendance registers, capture daily attendance sheets, and monitor attendance lines for each student.',
    metrics: [
      { label: 'Primary focus', value: 'Daily attendance' },
      { label: 'Core records', value: 'Registers and sheets' },
      { label: 'Tracking model', value: 'Kiosk-ready workflow' },
    ],
    workflow: [
      'Create a register for the relevant course and batch.',
      'Open a dated attendance sheet and launch attendance start mode.',
      'Mark the sheet as taken and review the attendance lines for detail-level history.',
    ],
    sections: [
      {
        id: 'register-management',
        title: 'Register Management',
        body: [
          'Attendance registers define the structure used to collect attendance against a batch and course.',
        ],
        bullets: [
          'Capture register name, code, course, and batch.',
          'Use separate registers when attendance rules differ by academic group.',
        ],
      },
      {
        id: 'attendance-sheets',
        title: 'Attendance Sheets',
        body: [
          'Attendance sheets are day-specific attendance records connected to a register, session, and date.',
        ],
        bullets: [
          'Select the register, name, session, date, and student list for the sheet.',
          'Use the kiosk-oriented workflow to move from Draft to Attendance Start and then Attendance Taken.',
        ],
      },
      {
        id: 'attendance-analysis',
        title: 'Attendance Analysis',
        body: [
          'Detailed attendance lines and attendance types support operational visibility and reporting.',
        ],
        bullets: [
          'Review individual attendance line records for each student entry.',
          'Configure attendance types such as regular class, exam, workshop, or seminar.',
        ],
      },
    ],
    related: ['timetable', 'student', 'exam'],
  },
  {
    slug: 'calendar',
    route: '/calendar',
    title: 'Calendar',
    icon: 'HiOutlineCalendar',
    category: 'Collaboration',
    format: 'DOCX',
    description:
      'Schedule classes, meetings, and reminders with a central event calendar that can sync with external providers.',
    metrics: [
      { label: 'Primary focus', value: 'Planning and events' },
      { label: 'Core records', value: 'Meetings and reminders' },
      { label: 'Integration', value: 'Google and Outlook sync' },
    ],
    workflow: [
      'Create a meeting or event from the calendar workspace.',
      'Set the subject, time, attendees, location, and reminders.',
      'Enable sync settings when users need real-time updates from Outlook or Google Calendar.',
    ],
    sections: [
      {
        id: 'event-planning',
        title: 'Event Planning',
        body: [
          'The calendar workspace helps teams schedule academic sessions, meetings, and operational deadlines with a single timeline view.',
        ],
        bullets: [
          'Create events with subject, date, time, location, and attendee details.',
          'Use day, week, month, or broader calendar views to plan workload and availability.',
        ],
      },
      {
        id: 'calendar-settings',
        title: 'Calendar Settings',
        body: [
          'Configuration settings allow the organization to connect external calendars for smoother coordination across platforms.',
        ],
        bullets: [
          'Enable synchronization with Outlook and Google Calendar.',
          'Keep event updates aligned between EduX and external scheduling tools.',
        ],
      },
      {
        id: 'reminders',
        title: 'Reminders and Alarms',
        body: [
          'Reminder rules help users receive notifications before an event starts so follow-ups are not missed.',
        ],
        bullets: [
          'Configure reminder types such as notification or email.',
          'Trigger reminders minutes, hours, or days before the event.',
        ],
      },
    ],
    related: ['discuss', 'to-dos', 'timetable'],
  },
  {
    slug: 'discuss',
    route: '/discuss',
    title: 'Discuss',
    icon: 'HiOutlineChatBubbleLeftRight',
    category: 'Collaboration',
    format: 'DOCX',
    description:
      'Support real-time internal communication with inboxes, direct messages, channels, mentions, and meetings.',
    metrics: [
      { label: 'Primary focus', value: 'Internal messaging' },
      { label: 'Core records', value: 'Inbox, channels, DMs' },
      { label: 'Real-time tools', value: 'Mentions and meetings' },
    ],
    workflow: [
      'Open the Discuss module to review inbox activity and notifications.',
      'Create channels or start direct conversations with staff and faculty members.',
      'Use mentions, notifications, and meetings to keep communication actionable.',
    ],
    sections: [
      {
        id: 'inbox-and-dms',
        title: 'Inbox and Direct Messages',
        body: [
          'The Discuss landing page centralizes incoming messages, starred items, history, and direct conversations for daily communication.',
        ],
        bullets: [
          'Read and reply to inbox messages from one place.',
          'Use direct messages for one-to-one communication between users.',
        ],
      },
      {
        id: 'channels',
        title: 'Channels and Mentions',
        body: [
          'Channels make group collaboration easier by giving teams a shared place for updates and decisions.',
        ],
        bullets: [
          'Create channels and add members for department or topic-based discussions.',
          'Use @mentions to notify specific participants inside a conversation.',
        ],
      },
      {
        id: 'notifications-and-meetings',
        title: 'Notifications and Meetings',
        body: [
          'Discuss is also the notification layer for new activity, and it can initiate meetings when live collaboration is needed.',
        ],
        bullets: [
          'Review the notification area for message and activity alerts.',
          'Launch meetings directly from the communication workflow.',
        ],
      },
    ],
    related: ['calendar', 'to-dos', 'hr'],
  },
  {
    slug: 'exam',
    route: '/exam',
    title: 'Exam',
    icon: 'HiOutlineCheckBadge',
    category: 'Academics',
    format: 'DOCX',
    description:
      'Configure exam types and rooms, build exam sessions, and manage grading-ready exam records by subject.',
    metrics: [
      { label: 'Primary focus', value: 'Exam governance' },
      { label: 'Core records', value: 'Types, rooms, sessions, exams' },
      { label: 'Evaluation', value: 'Normal and grade modes' },
    ],
    workflow: [
      'Configure exam types, rooms, and grade rules.',
      'Create an exam session for the relevant course, batch, dates, and venue.',
      'Add subject-wise exams under the session and evaluate results through the defined grading model.',
    ],
    sections: [
      {
        id: 'exam-configuration',
        title: 'Exam Configuration',
        body: [
          'Configuration controls make the exam module adaptable to different academic structures and assessment styles.',
        ],
        bullets: [
          'Manage exam types and room definitions with capacity controls.',
          'Define grade ranges with minimum and maximum percentages and the displayed result.',
        ],
      },
      {
        id: 'exam-sessions',
        title: 'Exam Sessions',
        body: [
          'Exam sessions group a complete exam cycle for a course and batch, including the timeline, type, venue, and evaluation method.',
        ],
        bullets: [
          'Set session name, code, start date, end date, course, batch, exam type, and evaluation type.',
          'Use the venue field to assign the configured room.',
        ],
      },
      {
        id: 'exams-and-subjects',
        title: 'Exams and Subjects',
        body: [
          'Each subject-level exam is stored separately under the broader exam session for clearer control and reporting.',
        ],
        bullets: [
          'Capture exam name, exam code, exam session, and linked subject.',
          'Maintain subject-level assessment clarity without losing session-level oversight.',
        ],
      },
    ],
    related: ['attendance', 'assignments', 'timetable'],
  },
  {
    slug: 'expenses',
    route: '/expenses',
    title: 'Expenses',
    icon: 'HiOutlineWallet',
    category: 'Finance & Procurement',
    format: 'DOCX',
    description:
      'Record employee expenses, manage approval stages, group expenses into reports, and configure categories and activities.',
    metrics: [
      { label: 'Primary focus', value: 'Expense control' },
      { label: 'Core records', value: 'Expenses and reports' },
      { label: 'Approval model', value: 'To report to done' },
    ],
    workflow: [
      'Employees create expenses and group them into reports.',
      'Managers review and approve reports through the defined status flow.',
      'Accounting posts the report and completes reimbursement.',
    ],
    sections: [
      {
        id: 'employee-expenses',
        title: 'Employee Expenses',
        body: [
          'The module helps institutions capture travel, food, office supply, and similar employee-driven business expenses.',
        ],
        bullets: [
          'Use My Expenses for individual entries and My Reports to package them for approval.',
          'Track both employee-created records and manager-facing expense reports.',
        ],
      },
      {
        id: 'approval-states',
        title: 'Approval States',
        body: [
          'Expense progress is transparent because every record moves through a named approval and posting state.',
        ],
        bullets: [
          'Follow the sequence from To Report and To Submit through Submitted, Approved, Posted, and Done.',
          'Use each state to understand whether the expense is editable, pending approval, posted to accounting, or reimbursed.',
        ],
      },
      {
        id: 'configuration-and-activities',
        title: 'Configuration and Activities',
        body: [
          'Configuration supports consistent categorization and task follow-up around each report.',
        ],
        bullets: [
          'Define expense categories to classify reimbursement items properly.',
          'Configure activity types for reminders, approvals, and review tasks.',
        ],
      },
    ],
    related: ['invoice', 'payroll', 'hr'],
  },
  {
    slug: 'faculty',
    route: '/faculty',
    title: 'Faculty',
    icon: 'HiUser',
    category: 'Academics',
    format: 'DOCX',
    description:
      'EduX is a fully integrated application that helps manage faculty profiles, personal information, subjects, salary records, library details, sessions, media movement, and employee creation.',
    metrics: [
      { label: 'Primary focus', value: 'Faculty management' },
      { label: 'Core records', value: 'Profiles, subjects, salary' },
      { label: 'Automation', value: 'Create Employee flow' },
    ],
    workflow: [
      'Go to the Faculties menu and click New to create a new faculty profile.',
      'Fill in the faculty form and maintain the Personal Information, Subjects, Salary Payment, Library, Sessions, and Media details.',
      'Use Create Employee to open the wizard and automatically populate the linked employee field.',
    ],
    sections: [
      {
        id: 'introduction',
        title: 'Faculty',
        body: [
          'EduX is fully integrated application which provides a facility to manage the faculty information. Go to menu Faculties.',
          'Click on New to create a new faculty profile.',
        ],
        figure: {
          src: '/assets/doc-images/faculty/image1.png',
          alt: 'Faculty list showing title, name, department, blood group, and emergency contact',
          caption:
            'Faculty list view showing a created faculty record with title, name, department, blood group, and emergency contact.',
        },
      },
      {
        id: 'faculty-form-details',
        title: 'Details of Faculty Form',
        body: [
          'The faculty form is used to capture the basic identity details of the faculty before maintaining operational records in the tabs.',
        ],
        figure: {
          src: '/assets/doc-images/faculty/image2.png',
          alt: 'Faculty form with personal information tab and profile details',
          caption:
            'Faculty form showing the photo area, title, faculty name fields, and the Personal Information tab with profile details.',
        },
        bullets: [
          'Photo: you can upload the photograph of a faculty using image widget at the top-left corner.',
          'Title: provide the appropriate title for the faculty from the list of titles.',
          'Name: assign values in First Name, Middle Name and Last Name to specify the name of the faculty.',
        ],
      },
      {
        id: 'pages-in-faculty-form',
        title: 'Pages in Faculty Form',
        body: [
          'The faculty form contains dedicated pages where personal, academic, payroll, library, and activity-related details are maintained.',
        ],
        subsections: [
          {
            id: 'personal-information',
            title: 'Personal Information',
            body: [
              'You can specify the personal details of the faculty under this page.',
            ],
            figure: {
              src: '/assets/doc-images/faculty/image2.png',
              alt: 'Personal information tab showing faculty personal details',
              caption:
                'Personal Information tab showing faculty contact, birth date, nationality, language, blood group, visa information, and emergency contact.',
            },
            bullets: [
              'Basic Information: under Basic Information, provide details like Gender, Phone, Mobile number and Email of the faculty.',
              'Birth Date: specify the birth date of the faculty.',
              'Blood Group: specify the blood group of the faculty.',
              'Nationality: specify the nationality of the faculty.',
              'Visa Info: add visa related textual description in Visa Info field.',
              'Language: you can specify the primary language of the faculty.',
              'Emergency Contact: it is the list of the users in the system. You can specify one from the list who can be contacted in emergency.',
            ],
          },
          {
            id: 'subjects-detail',
            title: 'Subjects Detail',
            body: [
              'You can specify the list of subjects that the faculty teaches under this tab.',
            ],
            figure: {
              src: '/assets/doc-images/faculty/image4.png',
              alt: 'Subjects tab showing subjects taught by the faculty member',
              caption:
                'Subjects tab listing the subjects taught by the faculty together with code, type, subject type, and grade weightage.',
            },
          },
          {
            id: 'salary-payment',
            title: 'Salary Payment',
            body: [
              'Salary Payment Tab',
              'This section manages salary records of faculty.',
              'Purpose: maintains salary payment history of faculty members.',
            ],
            figure: {
              src: '/assets/doc-images/faculty/imagec.png',
              alt: 'Salary payment tab with faculty monthly salary details',
              caption:
                'Salary Payment tab showing month and year, total salary, salary received, and total leaves taken for the faculty.',
            },
            bullets: [
              'Month and Year',
              'Total Salary',
              'Salary Received',
              'Total Leaves Taken',
              'Add monthly salary entries.',
              'Monitor leave deductions.',
            ],
          },
          {
            id: 'library-detail',
            title: 'Library Detail',
            body: [
              'Faculty may have a record in Library management system as they also need to use the library functionality.',
            ],
            figure: {
              src: '/assets/doc-images/faculty/image5.png',
              alt: 'Library tab showing the faculty library card reference',
              caption:
                'Library tab showing the library card linked to the faculty profile.',
            },
          },
          {
            id: 'sessions',
            title: 'Sessions',
            body: [
              'Whenever a session is created for this faculty, record is added under this tab. So you can have quick reference to all the sessions related details of the faculty.',
            ],
            gallery: [
              {
                src: '/assets/doc-images/faculty/image7.png',
                alt: 'Faculty smart buttons showing sessions and media counts',
                caption: 'Smart buttons showing the number of linked Sessions and Media records.',
              },
              {
                src: '/assets/doc-images/faculty/image8.png',
                alt: 'Session list related to the faculty member',
                caption:
                  'Sessions list showing the faculty, batch, subject, classroom, start time, end time, and status.',
              },
            ],
          },
          {
            id: 'media',
            title: 'Media',
            body: [
              'The Media section in the Faculty module tracks issued books, due dates, return status, and penalty details for library management.',
            ],
            figure: {
              src: '/assets/doc-images/faculty/imaged.png',
              alt: 'Media movement records linked to the faculty member',
              caption:
                'Media section showing library card, media item, issue date, due date, return details, overdue amount, and penalty values.',
            },
          },
        ],
      },
      {
        id: 'create-employee',
        title: 'Create Employee',
        body: [
          'Clicking on Create Employee button, wizard will be displayed.',
          'When you click on button Create Employee in wizard, value of Employee field is set automatically as shown in image.',
        ],
        figure: {
          src: '/assets/doc-images/faculty/image9.png',
          alt: 'Faculty action menu showing the Create Employee option',
          caption:
            'Faculty profile action menu showing the Create Employee option before opening the wizard.',
        },
        gallery: [
          {
            src: '/assets/doc-images/faculty/image10.png',
            alt: 'Create Employee wizard for the faculty record',
            caption:
              'Create Employee wizard with the option to create a user along with the employee record.',
          },
          {
            src: '/assets/doc-images/faculty/image11.png',
            alt: 'Faculty profile after employee field has been populated',
            caption:
              'Faculty profile after employee creation, showing the HR Employee field filled automatically.',
          },
        ],
        bullets: [
          'Click Create Employee to open the wizard.',
          'Click Create Employee again in the wizard to set the Employee field automatically.',
        ],
      },
    ],
    related: ['hr', 'assignments', 'timetable'],
  },
  {
    slug: 'fleet',
    route: '/fleet',
    title: 'Fleet',
    icon: 'HiOutlineTruck',
    category: 'People & Operations',
    format: 'DOCX',
    description:
      'Manage vehicles, contracts, services, odometer tracking, and fleet master data for transport operations.',
    metrics: [
      { label: 'Primary focus', value: 'Vehicle management' },
      { label: 'Core records', value: 'Vehicles, contracts, services' },
      { label: 'Tracking', value: 'Usage and maintenance' },
    ],
    workflow: [
      'Maintain vehicle records and related contract data.',
      'Track maintenance services and odometer entries over time.',
      'Use configuration masters to standardize manufacturers, models, and related fleet metadata.',
    ],
    sections: [
      {
        id: 'vehicles-and-contracts',
        title: 'Vehicles and Contracts',
        body: [
          'The fleet module centralizes vehicle records and the contracts that keep them compliant and operational.',
        ],
        bullets: [
          'Store each vehicle with the details needed for company transport use.',
          'Manage insurance, leasing, maintenance, and service contracts from one flow.',
          'Track contract states such as Incoming, In Progress, Expired, and Closed.',
        ],
      },
      {
        id: 'services-and-odometer',
        title: 'Services and Odometer',
        body: [
          'Service and odometer entries provide the operational history required for maintenance planning and cost visibility.',
        ],
        bullets: [
          'Record service work such as oil changes, repairs, and general maintenance.',
          'Track service states from New to Running, Done, or Cancelled.',
          'Store odometer readings to understand distance, usage, and service intervals.',
        ],
      },
      {
        id: 'fleet-configuration',
        title: 'Fleet Configuration',
        body: [
          'Configuration keeps vehicle models, manufacturers, and other master definitions standardized across the organization.',
        ],
        bullets: [
          'Maintain manufacturers and model definitions from the configuration area.',
          'Use master data to keep fleet reporting and setup consistent.',
        ],
      },
    ],
    related: ['expenses', 'hr', 'inventory'],
  },
  {
    slug: 'hr',
    route: '/hr',
    title: 'Human Resources',
    icon: 'HiOutlineBriefcase',
    category: 'People & Operations',
    format: 'DOCX',
    description:
      'Manage employee records, resume history, work information, private information, contracts, and departments.',
    metrics: [
      { label: 'Primary focus', value: 'Employee administration' },
      { label: 'Core records', value: 'Employees and contracts' },
      { label: 'Structure', value: 'Departments and hierarchy' },
    ],
    workflow: [
      'Create or update employee records with work and private details.',
      'Maintain resume entries, HR settings, and contract information for each employee.',
      'Organize the workforce through departments and reporting structures.',
    ],
    sections: [
      {
        id: 'employees',
        title: 'Employees',
        body: [
          'Employees are the central record in the HR module and hold personal, organizational, and professional history data.',
        ],
        bullets: [
          'Manage employee contact data, role information, and employment details.',
          'Use resume lines to capture education, work experience, certifications, and achievements.',
        ],
      },
      {
        id: 'employee-information',
        title: 'Work and Private Information',
        body: [
          'HR separates operational work settings from confidential personal records so teams can manage access appropriately.',
        ],
        bullets: [
          'Use Work Information for job location, schedule, company assignment, and approvals.',
          'Use Private Information for family data, identification numbers, contact details, and permit information.',
          'Review HR Settings for system-level employee configuration and module integration.',
        ],
      },
      {
        id: 'contracts-and-departments',
        title: 'Contracts and Departments',
        body: [
          'Contracts and departments translate employee records into a governed organizational structure.',
        ],
        bullets: [
          'Define contract salary structure, duration, allowances, deductions, and lifecycle state.',
          'Organize departments with managers and parent-child hierarchy relationships.',
        ],
      },
    ],
    related: ['payroll', 'time-off', 'faculty'],
  },
  {
    slug: 'inventory',
    route: '/inventory',
    title: 'Inventory',
    icon: 'HiOutlineSquares2X2',
    category: 'Finance & Procurement',
    format: 'DOCX',
    description:
      'Track stock movements, warehouse operations, replenishment, products, and inventory reporting in real time.',
    metrics: [
      { label: 'Primary focus', value: 'Stock control' },
      { label: 'Core records', value: 'Transfers and products' },
      { label: 'Planning', value: 'Replenishment and valuation' },
    ],
    workflow: [
      'Process receipts, deliveries, and inventory adjustments from operations.',
      'Monitor replenishment rules and run the scheduler when stock needs review.',
      'Use reports and configuration to manage warehouse-level accuracy.',
    ],
    sections: [
      {
        id: 'operations',
        title: 'Operations',
        body: [
          'Operational transfers support inbound, outbound, and corrective inventory actions from a single module.',
        ],
        bullets: [
          'Use receipts for vendor shipments and deliveries for customer dispatch.',
          'Adjust physical inventory when actual stock differs from system stock.',
          'Scrap unusable items and monitor replenishment suggestions when stock is low.',
        ],
      },
      {
        id: 'products-reporting',
        title: 'Products and Reporting',
        body: [
          'Products and reporting views make it easier to understand what is stored, where it is stored, and what it is worth.',
        ],
        bullets: [
          'Maintain product details including type, category, pricing, and stock quantity.',
          'Use stock, locations, moves history, and valuation reports for operational insight.',
        ],
      },
      {
        id: 'warehouse-configuration',
        title: 'Warehouse Configuration',
        body: [
          'Inventory configuration defines the underlying warehouse structure and product controls used by the business.',
        ],
        bullets: [
          'Manage warehouses and operation types for goods movement.',
          'Configure product categories and barcode nomenclatures for cleaner scanning and accounting.',
        ],
      },
    ],
    related: ['purchase', 'invoice', 'fleet'],
  },
  {
    slug: 'invoice',
    route: '/invoice',
    title: 'Invoice',
    icon: 'HiOutlineDocumentCurrencyDollar',
    category: 'Finance & Procurement',
    format: 'DOCX',
    description:
      'Handle customer invoices, payments, credit notes, vendor bills, and invoicing configuration from one finance workspace.',
    metrics: [
      { label: 'Primary focus', value: 'Billing and payments' },
      { label: 'Core records', value: 'Invoices, bills, payments' },
      { label: 'Coverage', value: 'Customer and vendor finance' },
    ],
    workflow: [
      'Create customer invoices, receive payments, and issue credit notes when needed.',
      'Record vendor bills, refunds, and employee expenses in the vendor flow.',
      'Apply payment terms and trade definitions from the invoicing configuration area.',
    ],
    sections: [
      {
        id: 'customer-invoicing',
        title: 'Customer Invoicing',
        body: [
          'The customer side of the invoice module manages sales billing, collections, and corrections for products or services.',
        ],
        bullets: [
          'Create invoices to bill customers and track outstanding amounts.',
          'Use credit notes to correct overcharges or reverse part of a sale.',
          'Record customer payments and maintain reusable customer and product masters.',
        ],
      },
      {
        id: 'vendor-finance',
        title: 'Vendor Finance',
        body: [
          'The vendor area covers outgoing liabilities and related payment data for suppliers and reimbursements.',
        ],
        bullets: [
          'Maintain bills, vendor refunds, vendor payments, and employee expenses.',
          'Store vendor bank accounts and vendor profiles to support accurate settlement.',
        ],
      },
      {
        id: 'invoice-configuration',
        title: 'Invoice Configuration',
        body: [
          'Configuration ensures invoices follow the right payment rules and delivery conventions.',
        ],
        bullets: [
          'Set payment terms to define due-date expectations.',
          'Use incoterms to clarify shipping and delivery responsibilities.',
        ],
      },
    ],
    related: ['purchase', 'expenses', 'inventory'],
  },
  {
    slug: 'library',
    route: '/library',
    title: 'Library',
    icon: 'HiOutlineBookOpen',
    category: 'Academics',
    format: 'DOCX',
    description:
      'Library management in EduX covers configuration masters, library cards, media and media units, return and issue operations, media movements, requests, and dashboard visibility for librarians.',
    metrics: [
      { label: 'Primary focus', value: 'Library operations' },
      { label: 'Core records', value: 'Cards, media, movements' },
      { label: 'Coverage', value: 'Students and faculty' },
    ],
    workflow: [
      'Configure media types, publishers, authors, and library card types before daily operations begin.',
      'Create library cards, media, and media units so issue and return activity can be tracked correctly.',
      'Monitor media movements, queue requests, purchase requests, and the library dashboard for ongoing control.',
    ],
    sections: [
      {
        id: 'library-configuration',
        title: 'Library Configuration',
        body: [
          'EduX provides the facility to configure library management.',
          'To begin this, you need to configure some data in order to make it working. We refer to this as Configuration.',
          'You can configure the data for library like Media Type, Publisher, Authors, and Library Card Type.',
        ],
        detailCards: [
          {
            title: 'Configuration masters',
            bullets: [
              'Media Type: that is assigned to a book, for example course book, reference book, or magazine.',
              'Publisher: list of publications of which books you have in library.',
              'Authors: list of authors whose books are available in library.',
              'Library Card Type: specify the type of library card, for example Bachelor Degree Card or Master Degree Card.',
            ],
          },
        ],
        subsections: [
          {
            id: 'publishers',
            title: 'Publishers',
            body: [
              'The media available in library may have different publishers. Sometimes book of particular publisher is preferred by teachers. In such cases you must have publisher related information.',
              'Go to Library -> Configuration -> Publishers. Click on New to create a new publisher.',
            ],
            figure: {
              src: '/assets/doc-images/library/image2.png',
              alt: 'Publisher record showing name, address, and linked media entries',
              caption:
                'Publisher record showing the publication name, address, and automatically linked media entries.',
            },
            bullets: [
              'Name: name of publication house.',
              'Address: name of company whose address is considered as address of the publication.',
              'Media(s): media are added in the list automatically when a media is created with particular publisher.',
            ],
          },
          {
            id: 'authors',
            title: 'Authors',
            body: [
              'Media is written by one or more authors. To have the details of authors while creating a media, you can configure the author details.',
              'Go to Library -> Configuration -> Authors. Click on New to create a new author.',
            ],
            figure: {
              src: '/assets/doc-images/library/image3.png',
              alt: 'Author record showing address and linked media entries',
              caption:
                'Author record showing the author name, address, and the automatically linked media list.',
            },
            bullets: [
              'Name: name of the author.',
              'Address: name of person or company whose address is referred as the address of the author.',
              'Media(s): list of medias written by the author. These lines are created automatically when a media is created.',
            ],
          },
          {
            id: 'media-type',
            title: 'Media Type',
            body: [
              'The Media Type are used for specifying the sub-category of the media, for example Course Book, Reference Books, or Magazine.',
              'Go to Library -> Configuration -> Media Type. Click on New to create a new media type.',
            ],
            figure: {
              src: '/assets/doc-images/library/image1.png',
              alt: 'Media type record showing name and code',
              caption:
                'Media Type record showing the media type name and code fields.',
            },
            bullets: [
              'Name: assign a name to a media type.',
              'Code: assign a code to a media type.',
            ],
          },
          {
            id: 'library-card-types',
            title: 'Library Card Types',
            body: [
              'Library cards can be available in different types as it can be used by students, faculties, alumni etc. Those library cards may have different rules application, so it is preferable that you configure this type of library cards.',
              'Go to Library -> Configuration -> Library Card Types. Click on New to create a new library card type.',
            ],
            figure: {
              src: '/assets/doc-images/library/imaged.png',
              alt: 'Library card type record showing media limits, duration, and penalty amount',
              caption:
                'Library Card Type record showing name, number of medias allowed, duration, and penalty amount per day.',
            },
            bullets: [
              'Name: title of library card type.',
              'Number of medias Allowed: specify the number of medias allowed on a card.',
              'Duration: number of days for which the media is allowed to be kept with the person.',
              'Penalty Amount Per Day: penalty given per day to the person who returns the media after due date.',
            ],
          },
        ],
      },
      {
        id: 'library-cards',
        title: 'Library Cards',
        body: [
          'We can create a library card and assign it to a person.',
          'Go to Library -> Configuration -> Library Cards.',
          'Click on New to create a new library card.',
        ],
        figure: {
          src: '/assets/doc-images/library/imagea.png',
          alt: 'Library card record assigned to a student',
          caption:
            'Library Card record showing the unique number, issue date, card type, and linked student details.',
        },
        gallery: [
          {
            src: '/assets/doc-images/library/imageb.png',
            alt: 'Student profile more menu showing the Library action',
            caption: 'Student profile menu showing the Library action from the More menu.',
          },
          {
            src: '/assets/doc-images/library/imagec.png',
            alt: 'Media movement details opened from the student library button',
            caption:
              'Media Movement details shown after clicking the Library button from the student profile.',
          },
        ],
        detailCards: [
          {
            title: 'Details of Library Cards Form',
            bullets: [
              'Number: number is the unique card number.',
              'Issue Date: issue date is on which date the card was issued to the person. By default todays date appears in this field.',
              'Card Type: type of card being created which can be selected or created from the list.',
              'Type: either faculty or student to whom the card is allocated. By default Student appears in this field.',
              'When a new library card is issued to a student, it can be allocated to student or faculty in their profile.',
              'After clicking the Library button, the system will display the Media Movement details.',
            ],
          },
        ],
      },
      {
        id: 'media-and-units',
        title: 'Media and Media Units',
        body: [
          'To accommodate all the media details available in your library, you can create records of the medias and individual media units for circulation.',
        ],
        subsections: [
          {
            id: 'media',
            title: 'Media',
            body: [
              'Go to Library -> All Media -> Media. Click on New to create a new media.',
            ],
            figure: {
              src: '/assets/doc-images/library/image9.png',
              alt: 'Media record showing authors, publishers, edition, and media metadata',
              caption:
                'Media record showing the media title, author, publisher, edition, page count, cost, and media type details.',
            },
            bullets: [
              'Name: provide the name of the book in Name field. It is mandatory field.',
              'Author(s): mention the name of authors who has written the book in Authors field. You can select multiple authors here.',
              'Publisher(s): mention the name of publication houses in the Publishers field.',
              'ISBN and Internal Code: provide ISBN and internal code of the book.',
              'Edition: specify the edition of the book.',
              'Tag(s): provide the appropriate tags in Tags field.',
              'Media Type: select media type.',
            ],
          },
          {
            id: 'media-unit',
            title: 'Media Unit',
            body: [
              'When a new media unit is created, OpenEduCat assumes that media unit is available in library. So now a librarian can perform operations like issue media unit and return media unit.',
              'Thus once a media unit is created, its default state is Available.',
              'Go to Library -> All Media -> Media Units. Click on New to create a new media unit.',
            ],
            gallery: [
              {
                src: '/assets/doc-images/library/imagee.png',
                alt: 'Media unit in available state ready for issue',
                caption:
                  'Media Unit in Available state before any issue activity is performed.',
              },
              {
                src: '/assets/doc-images/library/imagef.png',
                alt: 'Media unit in issued state with movement lines',
                caption:
                  'Media Unit in Issued state showing the linked movement line details.',
              },
            ],
            bullets: [
              'Name: provide the name for Media Unit.',
              'Media: select the media from list for which media unit is created.',
              'Barcode: provide the barcode of the media.',
              'Media Type: select media type.',
            ],
          },
          {
            id: 'return-media',
            title: 'Return Media',
            body: [
              'To return a media unit, click on Return Media button. It opens a wizard where you can mention the actual return date of the media unit. By default todays date appears in this field.',
              'Click on Proceed button to actually return a media unit. It closes the wizard and changes the state of media unit from Issued to Available.',
            ],
            gallery: [
              {
                src: '/assets/doc-images/library/image10.png',
                alt: 'Return Media wizard with actual return date field',
                caption:
                  'Return Media wizard used to enter the actual return date before proceeding.',
              },
              {
                src: '/assets/doc-images/library/image11.png',
                alt: 'Media unit after return process showing returned done state',
                caption:
                  'Media Unit after return processing, showing movement history and the Returned Done state.',
              },
            ],
          },
        ],
      },
      {
        id: 'media-movements',
        title: 'Media Movements',
        body: [
          'EduX records the movements of all the books in the system. Whenever any media unit is issued or returned its movement is recorded in the Media Movement.',
          'Go to Library -> All Media -> Media Movements. Click on New to create a new media movement.',
        ],
        figure: {
          src: '/assets/doc-images/library/image5.png',
          alt: 'Media movement record showing issued media details and student or faculty information',
          caption:
            'Media Movement record showing media, media unit, student or faculty details, issue date, due date, actual return date, and penalty fields.',
        },
        detailCards: [
          {
            title: 'Details of Media Movement',
            bullets: [
              'Media: select name of the media for media movement.',
              'Media Unit: select media unit for media movement.',
              'Media Type: select media type for media movement.',
              'Media Queue Count: total media requests for media.',
              'Invoice: penalty invoice reference.',
              'Library Card: library card to which media unit is issued.',
              'Student/Faculty: whether the media unit is issued to student or faculty.',
              'Student: based on selection in Student/Faculty name of student or faculty appears.',
              'Issue Date and Due Date: issue date and return date during which media unit is issued and is to be returned. By default todays date appears in issue date field.',
              'Actual Return Date: date during which media unit is returned by the holder.',
              'Penalty: in case the media unit is returned late penalty is calculated by EduX based on number of late days.',
            ],
          },
        ],
        subsections: [
          {
            id: 'movement-available',
            title: 'Available',
            body: [
              'Initially when a movement is created it is in Available state.',
            ],
          },
          {
            id: 'movement-issued',
            title: 'Issued',
            body: [
              'Click on Issue Media button to issue a media unit. It changes the state from Available to Issued.',
            ],
            figure: {
              src: '/assets/doc-images/library/image6.png',
              alt: 'Media unit workflow showing transition into issued state',
              caption:
                'Workflow actions on a media unit showing the state transition into Issued.',
            },
          },
          {
            id: 'movement-returned',
            title: 'Returned',
            body: [
              'If penalty is calculated then state changes from Issued to Returned.',
              'If media is returned after due date then penalty is calculated as shown in image.',
            ],
            gallery: [
              {
                src: '/assets/doc-images/library/image7.png',
                alt: 'Media movement state bar showing returned done status',
                caption: 'State bar showing the media movement reaching the returned stage.',
              },
              {
                src: '/assets/doc-images/library/image8.png',
                alt: 'Returned media record with penalty details',
                caption:
                  'Returned media record showing student details, dates, and calculated penalty values.',
              },
            ],
          },
        ],
      },
      {
        id: 'media-requests',
        title: 'Media Requests',
        body: [
          'There are two situations when you need to make a request for book.',
        ],
        bullets: [
          'A media is already allocated to someone else: create Media Queue Request.',
          'A media is not available in library: create a new Media Purchase Request.',
        ],
        subsections: [
          {
            id: 'media-queue-requests',
            title: 'Media Queue Requests',
            body: [
              'Go to Library -> All Media -> Media Queue Requests. Click on New to create a new media queue request.',
            ],
            figure: {
              src: '/assets/doc-images/library/image12.png',
              alt: 'Media queue request showing user, media, dates, and accepted workflow state',
              caption:
                'Media Queue Request showing user, media, from and to dates, and the Accepted workflow state.',
            },
            detailCards: [
              {
                title: 'Details of Media Queue Request Form',
                bullets: [
                  "Sequence Number: automatically generated sequence number by EduX. By default '/' appears here.",
                  'User: name of student or faculty who has requested for a media. By default the logged-in person appears here.',
                  'Media: name of the book.',
                  'From Date: date from which the person wants to have media. By default todays date appears here.',
                  'To Date: last date till which person can keep that media with him.',
                ],
              },
              {
                title: 'Workflow of Media Queue Request',
                bullets: [
                  'By default the state is Request which means the book request is just created.',
                  'Click on Accept button to accept request. It changes the state from Request to Accepted.',
                  'In case if you want to reject the request, click on Reject button. The state will be changed to Rejected.',
                  'If media is in reject state and you want to request it again click on Request Again.',
                ],
              },
            ],
          },
          {
            id: 'media-purchase-requests',
            title: 'Media Purchase Requests',
            body: [
              'Student or faculty can request for a new media. That is why you can create a new purchase media request.',
              'Go to Library -> All Media -> Media Purchase Requests.',
              'Click on New to create a new media purchase request.',
            ],
            figure: {
              src: '/assets/doc-images/library/image13.png',
              alt: 'Media purchase request showing title, media type, course, author, publisher, and draft workflow state',
              caption:
                'Media Purchase Request showing title, media type, course, subject, authors, publisher, requested by, edition, and the draft workflow state.',
            },
            detailCards: [
              {
                title: 'Details of Media Purchase Request Form',
                bullets: [
                  'Title: name of the media.',
                  'Media Type: select media type whether it is course book or reference book etc.',
                  'Course: name of the course whose students can use this media.',
                  'Subject: specify the subject of media.',
                  'Author(s): specify the name of author in Author field.',
                  'Publisher(s): name of the publisher of this media.',
                  'Requested By: select person who requested the media.',
                  'Edition: specify the edition of the media you want to have in library.',
                ],
              },
              {
                title: 'Workflow of Media Purchase Request',
                bullets: [
                  'Default state of the media request is Draft. Click on Request Media button for confirming the book request. It changes the state from Draft to Requested.',
                  'Click on Accept to accept the request. It changes the state from Requested to Accepted.',
                  'Click on Reject to reject the request.',
                ],
              },
            ],
          },
        ],
      },
      {
        id: 'library-dashboard',
        title: 'Library Dashboard',
        body: [
          'To see library dashboard go to Library -> Library Dashboard.',
          'Library dashboard is a graphical view for librarian. He can have quick overview of used medias of current day and current month.',
          'Librarian can see all the details of issued and available medias. These are fetched from media movements records.',
        ],
      },
    ],
    related: ['student', 'faculty', 'parent'],
  },
  {
    slug: 'parent',
    route: '/parent',
    title: 'Parent',
    icon: 'HiHomeModern',
    category: 'Academics',
    format: 'DOCX',
    description:
      'The Parent module in EduX ERP stores guardian records, captures relationship and identity details, and links each parent with the relevant student records.',
    metrics: [
      { label: 'Primary focus', value: 'Guardian records' },
      { label: 'Core records', value: 'Parents and students' },
      { label: 'Tracking', value: 'Relation and contact details' },
    ],
    workflow: [
      'Go to Parents and click New to create a new parent record.',
      'Enter the parent name, relationship with the student, email, and Aadhaar number.',
      'Link the parent to the required student records so contact and academic references stay connected.',
    ],
    sections: [
      {
        id: 'introduction',
        title: 'Introduction',
        body: [
          'Odoo allows you to store the details of the parents and their students.',
          'This module helps schools keep guardian information together with the student records that depend on it for communication and administration.',
        ],
      },
      {
        id: 'parent-record',
        title: 'Parent Record',
        body: [
          'Go to Parents and click New to create a new parent record.',
          'The parent form captures guardian details such as name, relation with student, email, Aadhaar number, and the list of linked students.',
        ],
        figure: {
          src: '/assets/doc-images/parent/image1.png',
          alt: 'Parent record showing guardian details and linked student records',
          caption:
            'Parent form showing the guardian profile, relation with student, contact information, Aadhaar number, and linked student entries.',
        },
        bullets: [
          'Store the parent name and contact information in one record.',
          'Set the relationship between the guardian and the student.',
          'Maintain linked student details directly from the Student(s) section.',
        ],
      },
      {
        id: 'parent-fields',
        title: 'Details of Parent',
        body: [
          'The following fields are maintained while creating or reviewing a parent profile.',
        ],
        detailCards: [
          {
            title: 'Guardian information',
            bullets: [
              'Name: provide the name of the parent from the list.',
              'Relation with Student: select the relation of the user with the student.',
              'Email: provide the email of the parent.',
              'Aadhaar Number: provide the Aadhaar number.',
            ],
          },
          {
            title: 'Student linkage',
            bullets: [
              'Students: select the students for whom the parent is created.',
              'Use the linked student table to keep guardian and learner records connected.',
            ],
          },
        ],
      },
      {
        id: 'student-linkage',
        title: 'Student Linkage',
        body: [
          'Each parent record can be linked with student records so schools can manage contact and academic relationships more clearly.',
        ],
        bullets: [
          'Select the student set connected to the guardian profile.',
          'Use parent linkage to support communication, admissions follow-up, and profile completeness.',
        ],
      },
    ],
    related: ['student', 'admission'],
  },
  {
    slug: 'payroll',
    route: '/payroll',
    title: 'Payroll',
    icon: 'HiOutlineDocumentCurrencyDollar',
    category: 'People & Operations',
    format: 'DOCX',
    description:
      'Run contracts, salary attachments, payslips, payroll batches, and payroll reporting with structured salary rules.',
    metrics: [
      { label: 'Primary focus', value: 'Salary processing' },
      { label: 'Core records', value: 'Contracts and payslips' },
      { label: 'Governance', value: 'Rules and structures' },
    ],
    workflow: [
      'Maintain contracts and salary attachments before payroll runs.',
      'Generate employee payslips and batch runs for the selected payroll period.',
      'Review payroll reports and supporting configuration for complete control.',
    ],
    sections: [
      {
        id: 'contracts-and-attachments',
        title: 'Contracts and Salary Attachments',
        body: [
          'Contracts define the compensation agreement, while salary attachments control recurring legal or policy-driven deductions.',
        ],
        bullets: [
          'Track contract states including New, Running, Expired, and Cancelled.',
          'Manage salary attachment states and deduction lifecycle from creation to expiry.',
        ],
      },
      {
        id: 'payslips',
        title: 'Payslips and Batches',
        body: [
          'Payslips provide the period-specific salary result for each employee and can be grouped into payroll batches for scale.',
        ],
        bullets: [
          'Use Draft, Waiting, Done, and Rejected statuses to manage payslip processing.',
          'Generate batch runs when the organization needs to process many employees at once.',
        ],
      },
      {
        id: 'payroll-reporting',
        title: 'Reporting and Configuration',
        body: [
          'Reporting and configuration complete the payroll cycle by exposing salary totals and rule definitions.',
        ],
        bullets: [
          'Use payroll reports to review total employees and wage totals by period.',
          'Configure salary structures, unpaid work entry types, and salary rule categories for correct computation.',
        ],
      },
    ],
    related: ['hr', 'time-off', 'expenses'],
  },
  {
    slug: 'purchase',
    route: '/purchase',
    title: 'Purchase',
    icon: 'HiOutlineShoppingCart',
    category: 'Finance & Procurement',
    format: 'DOCX',
    description:
      'Run procurement from request for quotation through purchase orders, receipts, vendor bills, and vendor management.',
    metrics: [
      { label: 'Primary focus', value: 'Procurement workflow' },
      { label: 'Core records', value: 'RFQs, POs, receipts, bills' },
      { label: 'Financial closure', value: 'Vendor billing' },
    ],
    workflow: [
      'Create a request for quotation and confirm it into a purchase order.',
      'Receive products in inventory and validate receipt operations.',
      'Generate and settle vendor bills to complete the procurement lifecycle.',
    ],
    sections: [
      {
        id: 'rfq-and-po',
        title: 'RFQ and Purchase Orders',
        body: [
          'The purchase module begins with vendor-facing requests for quotation and matures into approved purchase orders.',
        ],
        bullets: [
          'Use RFQs to gather vendor pricing and availability.',
          'Confirm the order, send the PO by email, confirm receipt dates, or cancel and lock as needed.',
        ],
      },
      {
        id: 'receipts-and-validation',
        title: 'Receipts and Validation',
        body: [
          'Warehouse receipt operations are created automatically once the purchase order is confirmed.',
        ],
        bullets: [
          'Validate receipts to increase stock levels officially.',
          'Use print, label printing, and cancel actions when warehouse teams need supporting operations.',
          'Track receipt status through Draft, Ready, and Done.',
        ],
      },
      {
        id: 'vendor-bills',
        title: 'Vendor Bills and Masters',
        body: [
          'The final procurement steps connect the received goods to payable financial records and vendor data.',
        ],
        bullets: [
          'Create vendor bills, credit notes, and payment references with correct accounting dates and due dates.',
          'Maintain vendor records and product masters for a complete purchasing ecosystem.',
        ],
      },
    ],
    related: ['inventory', 'invoice', 'expenses'],
  },
  {
    slug: 'student',
    route: '/student',
    title: 'Student',
    icon: 'HiAcademicCap',
    category: 'Academics',
    format: 'DOCX',
    description:
      'Manage student profiles, personal details, emergency contacts, activities, assignments, parents, and registration records.',
    metrics: [
      { label: 'Primary focus', value: 'Student lifecycle' },
      { label: 'Core records', value: 'Profiles and registration' },
      { label: 'Connected modules', value: 'Activities, parents, assignments' },
    ],
    workflow: [
      'Create a student profile with identity, contact, and profile attributes.',
      'Track extracurricular activity, assignments, parents, and related actions from smart buttons.',
      'Use student registration to enroll the student into a course and academic batch.',
    ],
    sections: [
      {
        id: 'student-profile',
        title: 'Student Profile',
        body: [
          'The student form brings together personal details, emergency contacts, address data, identity fields, and access credentials.',
        ],
        bullets: [
          'Capture photo, title, full name, gender, birth date, nationality, language, blood group, and category.',
          'Maintain emergency contact, user mapping, address details, kiosk PIN, registration number, and library card.',
        ],
      },
      {
        id: 'activity-and-relationships',
        title: 'Activity and Relationships',
        body: [
          'Student records connect academic life to extracurricular activity, parental information, and assigned coursework.',
        ],
        bullets: [
          'Use smart buttons for activity logs, assignments, and parent relationships.',
          'Track activity type, participating faculty, date, and description from the Activity Logs area.',
        ],
      },
      {
        id: 'student-registration',
        title: 'Student Registration',
        body: [
          'Student registration formalizes academic enrollment by linking the student to a course and batch.',
        ],
        bullets: [
          'Use the registration reference number, course, batch, and unit load rules to define enrollment.',
          'Track registration status in a structured workflow-friendly record.',
        ],
      },
    ],
    related: ['parent', 'admission', 'library'],
  },
  {
    slug: 'timetable',
    route: '/timetable',
    title: 'Timetable',
    icon: 'HiOutlineCalendarDays',
    category: 'Academics',
    format: 'DOCX',
    description:
      'Generate timetable sessions by day, faculty, subject, classroom, and timing while keeping reporting and attendance visible.',
    metrics: [
      { label: 'Primary focus', value: 'Session planning' },
      { label: 'Core records', value: 'Generated timetable sessions' },
      { label: 'Visibility', value: 'Faculty session references' },
    ],
    workflow: [
      'Generate a timetable by course, batch, and date range.',
      'Map each day to timings, faculty, subject, and classroom assignments.',
      'Use session reports and faculty session views to review the resulting schedule.',
    ],
    sections: [
      {
        id: 'timetable-generation',
        title: 'Timetable Generation',
        body: [
          'Time table generation defines the full session plan for a course and batch across a selected date range.',
        ],
        bullets: [
          'Choose the course, batch, start date, and end date.',
          'Assign each day to a timing, faculty member, subject, and classroom.',
        ],
      },
      {
        id: 'reporting',
        title: 'Reports and Attendance',
        body: [
          'Reporting options help operational teams review the generated plan and connect it back to attendance activity.',
        ],
        bullets: [
          'Use Generate Session Report to download or view timetable details.',
          'Review attendance information through the linked attendance sheet access.',
        ],
      },
      {
        id: 'faculty-session-view',
        title: 'Faculty Session View',
        body: [
          'Faculty records include a dedicated sessions smart button so teachers can review only their assigned timetable items.',
        ],
        bullets: [
          'View subject, batch, classroom, date, and session time from the faculty perspective.',
          'Keep faculty-level planning clear without scanning the full institutional timetable.',
        ],
      },
    ],
    related: ['faculty', 'attendance', 'calendar'],
  },
  {
    slug: 'time-off',
    route: '/time-off',
    title: 'Time Off',
    icon: 'HiOutlineSun',
    category: 'People & Operations',
    format: 'DOCX',
    description:
      'Manage leave requests, allocations, balances, approvals, public holidays, and accrual planning for employees.',
    metrics: [
      { label: 'Primary focus', value: 'Leave management' },
      { label: 'Core records', value: 'Requests and allocations' },
      { label: 'Visibility', value: 'Calendar-based overview' },
    ],
    workflow: [
      'Employees request leave from My Time Off and review allocations.',
      'Managers and HR process requests and maintain leave balances from the management menu.',
      'Configuration controls leave types, accrual plans, and public holidays.',
    ],
    sections: [
      {
        id: 'employee-self-service',
        title: 'Employee Self-Service',
        body: [
          'The self-service area helps employees apply for leave and review how many days are still available to them.',
        ],
        bullets: [
          'Use My Time Off to submit leave requests.',
          'Review allocated balances from My Allocations.',
          'Use the dashboard and overview for a broader leave calendar perspective.',
        ],
      },
      {
        id: 'management-reporting',
        title: 'Management and Reporting',
        body: [
          'Managers and HR teams can process requests centrally and analyze leave data from structured reporting views.',
        ],
        bullets: [
          'Use Time Off and Allocations in the management area for approval and balance control.',
          'Review leave data by employee and by leave type from reporting.',
        ],
      },
      {
        id: 'leave-configuration',
        title: 'Configuration',
        body: [
          'Configuration makes the leave framework flexible enough for different policy models and holiday calendars.',
        ],
        bullets: [
          'Define time off types such as sick leave or casual leave.',
          'Use accrual plans and public holidays to automate and govern leave availability.',
        ],
      },
    ],
    related: ['hr', 'payroll', 'calendar'],
  },
  {
    slug: 'to-dos',
    route: '/to-dos',
    title: 'To-Dos',
    icon: 'HiOutlineClipboardDocumentCheck',
    category: 'Collaboration',
    format: 'DOCX',
    description:
      'Create tasks, schedule activities as deadlines, track completion, and manage work by time-based Kanban states.',
    metrics: [
      { label: 'Primary focus', value: 'Task management' },
      { label: 'Core records', value: 'Tasks and activities' },
      { label: 'Planning model', value: 'Inbox to later' },
    ],
    workflow: [
      'Create a task and assign responsibility.',
      'Schedule an activity to set a practical deadline and follow-up date.',
      'Complete or reschedule the task based on changing priorities.',
    ],
    sections: [
      {
        id: 'task-capture',
        title: 'Task Capture',
        body: [
          'The To-Do module gives teams a simple way to create and assign work items without losing visibility.',
        ],
        bullets: [
          'Create new tasks with a clear title and assigned user.',
          'Use tasks as a lightweight operating system for daily work.',
        ],
      },
      {
        id: 'activity-deadlines',
        title: 'Activity Deadlines',
        body: [
          'Deadlines are managed through scheduled activities, which makes task follow-up more consistent with the wider Odoo workflow model.',
        ],
        bullets: [
          'Track deadline labels such as Due in 5 days directly from the task.',
          'Use Mark Done, Edit, and Cancel to control task execution.',
        ],
      },
      {
        id: 'kanban-states',
        title: 'Kanban States',
        body: [
          'Tasks are organized by time horizon so users can prioritize work quickly from the board view.',
        ],
        bullets: [
          'Use Inbox, Today, This Week, This Month, and Later to manage urgency.',
          'Move tasks across time-based states as workload changes.',
        ],
      },
    ],
    related: ['calendar', 'discuss', 'hr'],
  },
];

const categoryOverrides = {
  admission: 'Academic',
  assignments: 'Academic',
  attendance: 'Academic',
  calendar: 'Academic',
  exam: 'Academic',
  faculty: 'Academic',
  library: 'Academic',
  parent: 'Academic',
  student: 'Academic',
  timetable: 'Academic',
  expenses: 'Finance',
  invoice: 'Finance',
  hr: 'People',
  payroll: 'People',
  'time-off': 'People',
  discuss: 'Operations',
  fleet: 'Operations',
  inventory: 'Operations',
  purchase: 'Operations',
  'to-dos': 'Operations',
};

function flattenDetailCards(items = []) {
  return items.flatMap((item) => [item.title, ...(item.body || []), ...(item.bullets || [])]);
}

function flattenFigures(figure) {
  if (!figure) {
    return [];
  }

  return [figure.alt, figure.caption].filter(Boolean);
}

function flattenGallery(items = []) {
  return items.flatMap((item) => [item.alt, item.caption].filter(Boolean));
}

function flattenSections(sections = []) {
  return sections.flatMap((section) => [
    section.title,
    ...(section.body || []),
    ...(section.steps || []),
    ...(section.bullets || []),
    ...flattenFigures(section.figure),
    ...flattenGallery(section.gallery),
    ...flattenDetailCards(section.detailCards),
    ...flattenSections(section.subsections),
  ]);
}

function normalizeSearchText(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export function getSearchTokens(value) {
  const normalized = normalizeSearchText(value);
  return normalized ? normalized.split(/\s+/) : [];
}

export function getDocumentSearchText(document) {
  return normalizeSearchText(
    [
      document.title,
      document.category,
      document.description,
      ...(document.metrics || []).flatMap((metric) => [metric.label, metric.value]),
      ...(document.workflow || []),
      ...flattenSections(document.sections),
    ]
      .filter(Boolean)
      .join(' ')
  );
}

export function matchesDocumentSearch(document, query) {
  const tokens = getSearchTokens(query);

  if (!tokens.length) {
    return true;
  }

  const searchText = getDocumentSearchText(document);
  return tokens.every((token) => searchText.includes(token));
}

documents.forEach((document) => {
  document.category = categoryOverrides[document.slug] || document.category;
});

export const documentsBySlug = Object.fromEntries(
  documents.map((document) => [document.slug, document])
);

export const documentsByCategory = categoryOrder
  .map((category) => ({
    category,
    items: documents.filter((document) => document.category === category),
  }))
  .filter((group) => group.items.length > 0);

export const featuredDocuments = [
  'faculty',
  'student',
  'payroll',
  'inventory',
  'calendar',
  'admission',
].map((slug) => documentsBySlug[slug]);
